import * as z from 'zod/mini'
import { endAfterStart } from '../../schemas/common'
import { entityFields, openingHours } from '../../schemas/entity'
import { entityPostFields } from '../../schemas/entity-post'
import { PublicPageView } from '../../types/enums/public-page-view.enum'

const optionalText = z.optional(z.string().check(z.trim(), z.maxLength(255)))

/** `PATCH manage/entities/:entityId` - every field optional; only what is sent changes. */
export const editEntityBody = z.object({
  name: z.optional(entityFields.name),
  description: z.optional(entityFields.description),
  shortDescription: z.optional(entityFields.shortDescription),
  phone: z.optional(entityFields.phone),
  email: z.optional(entityFields.email),
  /** `null` removes the opening hours; a day missing from the object is closed. */
  openingHours: z.nullish(openingHours),
  /** Whether the hours are published. `false` hides them publicly but keeps them stored. */
  openingHoursEnabled: z.optional(z.boolean()),
  addressLine1: optionalText,
  city: optionalText,
  state: optionalText,
  zip: optionalText,
  public: z.optional(z.boolean()),
  showCalendar: z.optional(z.boolean()),
  showReservations: z.optional(z.boolean()),
  defaultPublicView: z.optional(z.enum(PublicPageView)),
  /** Oznamy - paywalled. Turning it on requires the Announcements feature. */
  showPublicBanner: z.optional(z.boolean()),
  /** Origins allowed to embed this premise's public elements. Replaces the whole list when sent. */
  allowedOrigins: z.optional(entityFields.allowedOrigins),
  companyName: optionalText,
  companyId: optionalText,
  vatId: optionalText,
  iban: optionalText,
})
export type EditEntityBody = z.infer<typeof editEntityBody>

const bankCode = z.optional(z.string().check(z.trim(), z.minLength(2), z.maxLength(40)))

export const setEntityPaymentSettingsBody = z.object({
  iban: bankCode,
  swift: bankCode,
  invoiceTemplate: z.optional(z.string()),
})
export type SetEntityPaymentSettingsBody = z.infer<typeof setEntityPaymentSettingsBody>

/**
 * `PUT manage/entities/:entityId/post` - the whole announcement, replaced in one request. `hideAfter` must be
 * after `showFrom` when both are set.
 */
export const putEntityPostBody = z
  .object({
    title: entityPostFields.title,
    content: entityPostFields.content,
    showFrom: entityPostFields.showFrom,
    hideAfter: entityPostFields.hideAfter,
  })
  .check(z.superRefine(endAfterStart('showFrom', 'hideAfter')))
export type PutEntityPostBody = z.infer<typeof putEntityPostBody>

/** The premise in a `manage/entities/:entityId/...` URL, checked by `ManagedEntityGuard`. */
export const managedEntityParams = z.object({ entityId: z.uuid() })
export type ManagedEntityParams = z.infer<typeof managedEntityParams>
